import { useCallback, useEffect, useState } from 'react';

import {
  TransactionStatus,
  useLoyaltyTransactionStatusQuery,
  useOrderSelectionQuery,
} from 'generated/graphql-gateway';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useLocalStorageState } from 'hooks/use-local-storage-state';
import { StorageKeys } from 'utils/local-storage';

import {
  IUseShortCodePollStatus,
  IUseShortCodePollStatusParams,
  ShortCodePollingStatus,
} from './types';

export const useShortCodePollStatus = ({
  loyaltyId,
  skipPolling,
  shortCode,
  pollInterval,
}: IUseShortCodePollStatusParams): IUseShortCodePollStatus => {
  const [pollingStatus, setPollingStatus] = useState<ShortCodePollingStatus>(
    ShortCodePollingStatus.None
  );
  const [
    { shortCodeTransactionId },
    setShortCodeTransaction,
    cleanShortCodeTransaction,
  ] = useLocalStorageState<{
    shortCodeTransactionId?: string;
  }>({
    key: StorageKeys.IN_RESTAURANT_REDEMPTION_SHORT_CODE_POLLING_STATUS,
    defaultReturnValue: {},
  });

  const resetPollingStatus = useCallback(() => {
    setPollingStatus(ShortCodePollingStatus.None);
    cleanShortCodeTransaction();
  }, [cleanShortCodeTransaction]);

  // setting pending code state
  useEffect(() => {
    if (!skipPolling) {
      setPollingStatus(ShortCodePollingStatus.Pending);
    }
  }, [skipPolling]);

  // reset the transaction if the short code changes
  useEffectOnUpdates(() => {
    if (shortCodeTransactionId) {
      cleanShortCodeTransaction();
    }
  }, [shortCode]);

  const skip =
    skipPolling || !shortCode || !loyaltyId || pollingStatus === ShortCodePollingStatus.None;

  // short code polling
  const { data: pollShortCodeData } = useOrderSelectionQuery({
    variables: {
      otp: shortCode!,
    },
    fetchPolicy: 'no-cache',
    skip,
    pollInterval: skip || pollingStatus !== ShortCodePollingStatus.Pending ? 0 : pollInterval,
  });

  const pollShortCodeTransaction = pollShortCodeData?.orderSelection?.transactionId;
  useEffect(() => {
    if (pollShortCodeTransaction) {
      setPollingStatus(ShortCodePollingStatus.Processing);
      setShortCodeTransaction({ shortCodeTransactionId: pollShortCodeTransaction });
    }
  }, [pollShortCodeTransaction, setShortCodeTransaction]);

  // short code transaction polling
  const { data: pollTransactionStatusData } = useLoyaltyTransactionStatusQuery({
    variables: {
      loyaltyId: loyaltyId!,
      transactionId: shortCodeTransactionId!,
    },
    fetchPolicy: 'no-cache',
    skip: skip || !shortCodeTransactionId,
    pollInterval: skip || pollingStatus !== ShortCodePollingStatus.Processing ? 0 : pollInterval,
  });

  const pollShortCodeTransactionStatus =
    pollTransactionStatusData?.loyaltyUser?.transactions?.[0]?.status;
  useEffect(() => {
    if (pollShortCodeTransactionStatus === TransactionStatus.CLAIMED) {
      setPollingStatus(ShortCodePollingStatus.Confirmed);
    }
  }, [pollShortCodeTransactionStatus]);

  return {
    pollingShortCodeStatus: pollingStatus,
    resetPollingStatus,
  };
};
